import styled from "styled-components";
import { button } from "../../lib/sharedStyles";

// Prevents layout thrashing.
export const OuterContainer = styled.div<{ $isStuck: boolean; $scrollToActivate: boolean }>`
  width: 100%;
  height: 66px;

  display: ${({ $scrollToActivate, $isStuck }) =>
    $scrollToActivate ? ($isStuck ? "block" : "none") : "block"};
`;

interface StickyContainerProps {
  $brand: string;
  $bottom: string;
  $isStuck: boolean;
  $isOpen: boolean;
  $scrollToActivate: boolean;
  $searchSpringContainerHeight: number;
  $stickToBottom: boolean;
  $top: string;
  $theme: string;
  $transparentBackground: boolean;
}

export const StickyContainer = styled.div<StickyContainerProps>`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 66px;
  display: flex;
  justify-content: center;
  transition: transform 600ms cubic-bezier(.38,.41,.27,1), opacity 300ms ease;

  ${({ $isStuck, $top }) => $isStuck && `
    position: fixed;
    top: 0;
    left: 0;
    z-index: 12;
    transform: translateY(${$top});
  `}

  ${({ $isOpen }) => $isOpen && `
    position: fixed;
    top: 0;
    left: 0;
    height: auto;
    z-index: 1001;
  `}

  ${({ $stickToBottom, $isOpen, $isStuck, $searchSpringContainerHeight }) => `
    @media (max-width: 1023px) {
      ${$stickToBottom ? `
        bottom: 0; /* Keep it stuck to the bottom */
        top: auto; /* Ensure it doesn't shift to the top */
        z-index: ${$isOpen ? 12 : 11};
        border-radius: 16px 16px 0px 0px;
        height: ${$isOpen ? 'auto' : '64px'}; /* Expand height when open */
      ` : `
        top: ${$isOpen || $isStuck ? '0' : `${$searchSpringContainerHeight}px`};
        z-index: ${$isOpen ? 12 : 11};
      `}
    }
  `}

  ${({ $theme, $brand }) => `

    background-color: ${
      $brand == 'fender' ? ($theme == 'dark' ? '#191A1F' : '#FFF') :
      ($brand == 'presonus' || $brand == 'jackson') ? ($theme == 'dark' ? '#000' : '#FFF') :
      'inherit'
    };
  `}

  ${({ $transparentBackground, $isStuck, $isOpen }) => $transparentBackground && !$isStuck && !$isOpen && `
    background-color: transparent;
  `}

  // Hide the nav if scrollToActivate is enabled and it's not stuck yet
  ${({ $isStuck, $scrollToActivate }) => $scrollToActivate && !$isStuck && `
    opacity: 0;
    pointer-events: none;
  `}

  // Show when it becomes stuck
  ${({ $isStuck, $scrollToActivate }) => $scrollToActivate && $isStuck && `
    opacity: 1;
    pointer-events: auto;
  `}
`;

export const InnerContainer = styled.div`
  height: 64px;
  width: 100%;
  max-width: 1440px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
  padding: 0 16px;
`;

export const Logo = styled.div`
  display: flex;
  
  img {
    width: 100%;
    max-width: 200px;
  }
`;

export const CTA = styled.a<{ $brand: string; $theme: string }>`
  ${({ $brand }) => button('Blue Pill', $brand)}
  padding: 5px;
  min-width: 100px;
  font-size: 14px;
  white-space: nowrap;
  
  @media (min-width: 768px) and (max-width: 1023px) {
    min-width: 140px;
  }

  ${({ $brand, $theme }) => $brand == 'fender' && `
    padding: 10px 24px;
    background-color: ${$theme == 'dark' ? '#FFF' : '#191A1F'};
    color: ${$theme == 'dark' ? '#191A1F' : '#FFF'};

    &:hover {
      background-color: ${$theme == 'dark' ? '#F7F7F7' : '#333'};
      color: ${$theme == 'dark' ? '#191A1F' : '#FFF'};
    }
  `}
`;

export const LeftContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const RightContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const LinkList = styled.div<{ $isOpen: boolean; $includeMargin: boolean; $theme: string; $brand: string; $stickToBottom: boolean }>`
  display: none;

  ${({ $isOpen, $theme, $brand, $stickToBottom }) => $isOpen && `
    @media (max-width: 1023px) {
      display: block;
      position: absolute;
      width: 100vw;
      right: -16px;
      top: 52px;
      padding: 0 16px;
      border-radius: ${$stickToBottom ? '16px 16px 0px 0px' : '0px 0px 16px 16px'};
      background-color: ${
        (() => {
          switch ($brand) {
            case 'fender':
              return $theme == 'dark' ? '#191A1F' : '#FFF';
            case 'presonus':
              return $theme == 'dark' ? '#000' : '#FFF';
            default:
              return '#000';
          }
        })()
      };
    }
  `}

  @media (max-width: 1023px) {
    ${({ $isOpen, $stickToBottom }) => $isOpen && $stickToBottom && `
      position: absolute;
      bottom: 100%; /* Push content upward */
      top: auto; /* Prevent it from being placed at the top */
      height: auto;
    `}
  }

  @media (min-width: 1024px) {
    display: flex;
    margin-right: 32px;
  }
  
  @media (min-width: 1280px) {
    margin-right: 64px;
  }
`;

export const Link = styled.a<{ $brand: string; $theme: string }>`
  display: flex;
  align-items: center;
  height: 64px;
  line-height: normal;
  text-decoration: none;
  padding: 0 8px;

  @media (max-width: 1023px) {
    height: 60px;
    padding: 0 16px;
  }

  ${({ $brand, $theme }) => $brand == 'fender' && `
    font-family: "Instrument Sans";
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.14px;
    color: ${$theme == 'dark' ? '#F5F5F5' : '#595959'};
    
    &:hover {
      color: ${$theme == 'dark' ? '#FFF' : '#000'};
    }
  `}

  ${({ $brand, $theme }) => $brand == 'presonus' && `
    font-size: 14px;
    color: ${$theme == 'dark' ? '#FFF' : '#000'};
    border-bottom: 4px solid transparent;

    &:hover {
      color: #0083ff;
    }

    @media (min-width: 1023px) {
      border-bottom: 1px solid #e0e0e0;
      
      &:first-of-type {
        border-top: 1px solid #e0e0e0;
      }

      &:hover {
        border-bottom-color: #0083ff;
        color: #0083ff;
      }
    }
  `}
`;

export const Toggle = styled.button<{ $theme: string; }>`
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  z-index: 20;
  position: relative;
  margin-right: 8px;

  svg {
    fill: ${({ $theme }) => ($theme == 'dark' ? '#FFF' : '#000')};
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

export const ModalBackDrop = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: black;
  opacity: 0.5;
  z-index: 5;
`;